import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { mixpanel } from "common/analytics";
import { useTranslation } from "common/locales";
import { StyledInputContainer } from "features/StyledInputContainer";
import {
  FileEntry,
  FileUpload,
  updateFileEntry,
  uploadFile,
  useAppDispatch,
  useAppSelector,
} from "model";
import { Dispatch, SetStateAction } from "react";
import AddNewFileDialog from "./AddNewFileDialog";
import { convertFromFormInput, FileEntryFormData } from "./FileEntryFormData";

export default function UpdateFileEntry({
  fileEntry,
  dialogOpen,
  setDialogOpen,
}: {
  fileEntry: FileEntry;
  dialogOpen: boolean;
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const user = useAppSelector((state) => state.user.details);
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleClose = () => {
    setDialogOpen(false);
    mixpanel.track("Update fieldPreparation event cancelled");
  };

  async function handleSave(fileEntryFormData: FileEntryFormData) {
    if (
      (!fileEntryFormData.file || !fileEntryFormData.fileName) &&
      !fileEntryFormData.fileUploadId
    ) {
      return;
    }

    if (!fileEntryFormData.fileUploadId) {
      if (!fileEntryFormData.file) {
        console.log("File content is missing, something went wrong");
        return;
      }
      const fileUpload = await dispatch(
        uploadFile({
          fileName: fileEntryFormData.fileName ?? "",
          file: fileEntryFormData.file,
        })
      );
      fileEntryFormData.fileUploadId = (fileUpload.payload as FileUpload).guid;
    }

    const convertedFileEntry = convertFromFormInput(
      fileEntryFormData,
      user?.guid ?? ""
    );

    await dispatch(
      updateFileEntry({
        ...convertedFileEntry,
        guid: fileEntry.guid,
      })
    );
    notify(t("updateFileEntry.add.success"));
    mixpanel.track("FileEntry updated");
    handleClose();
  }
  return (
    <Dialog
      open={dialogOpen}
      onClose={handleClose}
      fullScreen={isMobile}
      disableEscapeKeyDown={isMobile}
      maxWidth={isMobile ? undefined : "sm"}
      fullWidth={!isMobile}
      sx={
        isMobile
          ? undefined
          : {
              "& .MuiDialog-paper": {
                paddingTop: "24px",
              },
            }
      }
    >
      {isMobile ? (
        <StyledInputContainer isMobile={isMobile}>
          <AddNewFileDialog
            fileEntry={fileEntry}
            handleSave={handleSave}
            handleClose={() => {
              handleClose();
            }}
            title="Edit file entry"
          />
        </StyledInputContainer>
      ) : (
        <AddNewFileDialog
          fileEntry={fileEntry}
          handleSave={handleSave}
          handleClose={() => {
            handleClose();
          }}
          title="Edit file entry"
        />
      )}
    </Dialog>
  );
}
